import React, { useEffect, useMemo } from 'react';
import { usePopoverElectron } from 'shared/hooks/use-popover-electron';
import { NewGoalWithID } from 'shared/types/goal';
import { NewTagWithID } from 'shared/types/tag';
import { CreateTask as CreateTaskType, NewSubTask } from 'shared/types/task';
import { LoadingTaskCreate, TaskCreate } from 'shared/views/task-detail';

import { useTaskCreateData } from './hooks/use-task-create-data';

const noop = () => {};

export type CreateTaskLoaderProps = {
  name: string;
  updateName: (name: string) => void;
};

export const CreateTaskLoader: React.FC<CreateTaskLoaderProps> = ({
  name,
  updateName,
}) => {
  const { onClose } = usePopoverElectron();
  const _onClose = () => onClose?.();

  return (
    <LoadingTaskCreate
      onClose={_onClose}
      name={name}
      updateName={updateName}
      noBreakpoints
    />
  );
};

export type CreateTaskProps = {
  name: string;
  updateName: (name: string) => void;
};

export const CreateTask: React.FC<CreateTaskProps> = ({ name, updateName }) => {
  const data = useTaskCreateData();
  const { onClose, createTask, setHeight } = usePopoverElectron();

  const _onClose = () => onClose?.();

  const createAndClose = ({
    createTask: _createTask,
    subTasks,
    goalsToCreate,
    tagsToCreate,
  }: {
    createTask: CreateTaskType;
    subTasks: NewSubTask[];
    goalsToCreate: NewGoalWithID[];
    tagsToCreate: NewTagWithID[];
  }) => {
    if (_createTask) {
      createTask?.(_createTask, {
        subTasks,
        goals: goalsToCreate,
        tags: tagsToCreate,
      });
    }

    _onClose();
  };

  useEffect(() => {
    // track body height to update the popover
    const updateHeight = () => {
      const outerHeight = window.outerHeight;
      const scrollHeight = Math.max(1, document.body.scrollHeight);

      // The zoom factor is the ratio of the stable outer height
      // to the variable (scaled) scroll height.
      const zoomFactor = outerHeight / scrollHeight;

      // The correct height is the reported height scaled back up by the zoom factor.
      // This ensures the window is large enough for the now-zoomed content.
      const correctedHeight = outerHeight * zoomFactor;

      setHeight?.(correctedHeight);
    };
    updateHeight();
  }, [setHeight]);

  const contextualCreationData = useMemo(
    () => ({
      ...data,
      openPremiumDialog: noop,
    }),
    [data],
  );

  return (
    <TaskCreate
      name={name}
      updateName={updateName}
      onClose={_onClose}
      onCreate={createAndClose}
      contextualCreationData={contextualCreationData}
      noBreakpoints
    />
  );
};
